<template>
  <div class="cart-drawer critical-component-hide">
    <div class="cart-drawer__header">
      <div class="cart-drawer__title">
        <span
          class="
            cart-drawer__title-text
            text-heading-xl
          "
          v-text="$string('cart.title')"
        />
        <span
          class="
            cart-drawer__title-count
            text-body-xs-regular text-body-s-regular-desktop
          "
          v-text="`(${cart.item_count})`"
        />
      </div>

      <free-gift-notification
        :total="total"
      />
    </div>

    <form
      v-if="cart.item_count > 0 || cartStatus.label === 'adding'"
      class="cart-drawer__form"
      :action="`${$variable('routes.cart.url')}?locale=${$variable('store.language')}`"
      method="post"
    >
      <div
        class="cart-drawer__line-items"
        js-overlay="scrollElement"
      >
        <template
          v-for="(lineItem, index) of cartItems"
          :key="getCartItemKey(lineItem)"
        >
          <line-item
            class="cart-drawer__line-item"
            :free-gift-item="freeGiftItem"
            :line-item="lineItem"
            :total="total"
            template="cart-drawer"
          />

          <divider-line
            v-if="(index + 1) !== cartItems.length && (lineItem.properties && total > threshold)"
          />
        </template>
        <upsell-products />
      </div>

      <cart-summary
        v-if="cart.item_count > 0"
        class="cart-drawer__footer"
        template="cart-drawer"
      />
    </form>

    <div
      v-else
      class="cart-drawer__empty-container"
    >
      <div
        class="cart-drawer__empty"
      >
        <p
          class="
            text-body-s-regular
            text-body-l-regular-desktop
          "
          v-text="$string('cart.empty.drawer_text')"
        />

        <btn
          v-if="emptyCtaUrl && emptyCtaText"
          custom-element="a"
          class="cart-drawer__empty-cta"
          :label="emptyCtaText"
          show-icon
          modifiers="text block center"
          :url="emptyCtaUrl"
        />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Cart drawer (cart-drawer)
 * -----------------------------------------------------------------------------
 * Displays contents of cart.
 *
 * @param {Object} overlay - Overlay API.
 *
 */
import { mapActions, mapState } from 'vuex'

import Btn from '~global/btn/btn'
import CartSummary from '~async/cart-summary/cart-summary'
import DividerLine from '~async/utils/divider-line/divider-line'
import LineItem from '~async/line-item/line-item'
import FreeGiftNotification from '~async/free-gift-notification/free-gift-notification'
import UpsellProducts from '~async/upsell-products/upsell-products.vue'

export default {
  name: 'CartDrawer',

  components: {
    Btn,
    CartSummary,
    DividerLine,
    LineItem,
    FreeGiftNotification,
    UpsellProducts,
  },

  props: {
    overlay: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      emptyCtaUrl: window.cnvs.cart.empty.ctaUrl,
      emptyCtaText: window.cnvs.cart.empty.ctaText,
      threshold: window.cnvs.cart.freeGift.threshold,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      cartStatus: (state) => state.cart.status,
    }),

    /**
     * Calculate total price in cart if there is a free gift item
     */

    total() {
      let freeGiftPrice = 0
      if (this.freeGiftItem && this.freeGiftItem.discounted_price !== 0) {
        freeGiftPrice = this.freeGiftItem.discounted_price
      }
      const total = (this.cart.total_price - freeGiftPrice) / 100
      return total
    },

    /**
     * Compute cart items.
     * - For each item that is being added, check if it's already in the cart.
     * - Check against key (if adding from cart) or id (if adding from other).
     * - If it isn't then add a false item to act as a loading placeholder.
     * @returns {Array}
     */
    cartItems() {
      if (this.cartStatus.label !== 'adding') {
        return this.cart.items
      }

      const placeholders = []

      this.cartStatus.items.forEach((addedItem) => {
        const match = this.cart.items.find((cartItem) => {
          const cartItemProperty = typeof addedItem === 'string'
            ? cartItem.key
            : cartItem.id

          return cartItemProperty === addedItem
        })

        if (match) {
          return
        }

        placeholders.push(false)
      })

      return [
        ...placeholders,
        ...this.cart.items,
      ]
    },

    /**
     * Compute free gift item key.
     * - Check for item key if there is a cart_add_on property for 'Free Gift'
     * @returns {Number}
     */
    freeGiftItem() {
      const freeGiftItem = this.cartItems
        .filter(item => item.properties?.cart_add_on === 'Free Gift')
        .map(item => item)[0]

      return freeGiftItem || null
    },
  },

  watch: {

    /**
     * Watch cart items to update focus trap.
     */
    'cart.items'() {
      if (!this.overlay.isActive) {
        return
      }

      this.overlay.refresh({ currentElement: document.activeElement })

      if (this.total < this.threshold && this.freeGiftItem) {
        this.removeItem(this.freeGiftItem.key)
      }
    },
  },

  mounted() {
    if (!this.overlay.isActive) {
      return
    }

    this.overlay.refresh()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      removeItem: 'cart/updateItem',
    }),

    /**
     * Get cart item key.
     * @param {Object} item - Line item.
     * @param {Number} index - Line item index.
     * @returns {String}
     */
    getCartItemKey(item, index) {
      if (!item) {
        return `cart-line-item-loading-${index}`
      }

      return `cart-line-item-${item.key}`
    },

    /**
     * Shopify section editor select event.
     */
    shopifySectionSelect() {
      this.overlay.open({
        component: 'cart-drawer',
        ignoreDismissed: true,
      })
    },

    /**
     * Shopify section editor deselect event.
     */
    shopifySectionDeselect() {
      this.overlay.close()
    },
  },
}
</script>

<style lang="scss">
@import './cart-drawer';
</style>
